// Copyright ©️ 2026 eVolve MEP, LLC

import { useState, useMemo, useCallback } from 'react';

import type { ActiveAllFilter } from 'components/Mantine/GlobalFilters/ActiveAllSelect';
import type { WrappedSelectProps } from 'components/Mantine/TypeSafeSelect/types';
import type { WithRequired } from 'helpers/helper-types';

/**
 * A hook that manages the active/all filter state, and also helps manage the possible statuses/states based on the active/all filter state.
 * @param whenActive - The statuses/states that indicate an instance is active
 * @param allPossible - All possible statuses/states
 * @param initialState - The initial active/all filter state. Optional. Defaults to 'active'.
 */
export const useActiveAllFilterState = <T>(
  whenActive: T[],
  allPossible: T[],
  initialState: ActiveAllFilter = 'active',
) => {
  const [activeState, setActiveState] = useState<ActiveAllFilter>(initialState);
  const onActiveStateChange = useCallback((value: ActiveAllFilter | null) => setActiveState(value ?? 'active'), []);
  const filterValue = useMemo(
    () => (activeState === 'active' ? whenActive : allPossible),
    [activeState, whenActive, allPossible],
  );
  return [
    filterValue,
    {
      value: activeState,
      onChange: onActiveStateChange,
    } as WithRequired<Pick<WrappedSelectProps<ActiveAllFilter, object>, 'value' | 'onChange'>>,
  ] as const;
};
